import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Layers, BarChart3, LogOut, Play, Clock, Download, 
  User, ChevronRight, Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [platformsRes, generationsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/platforms`, { withCredentials: true }),
        axios.get(`${API_URL}/api/generations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/stats`, { withCredentials: true })
      ]);
      setPlatforms(platformsRes.data);
      setGenerations(generationsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E60000]/30 border-t-[#E60000] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="font-heading text-2xl font-black">
            Sente<span className="text-[#E60000]">tica</span>
          </a>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                <User size={16} />
              </div>
              <div>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-gray-500">Access your AI tools and manage your generations.</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#111] rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#E60000]/20 flex items-center justify-center">
                <Zap size={20} className="text-[#E60000]" />
              </div>
              <span className="text-xs text-gray-500">Usage</span>
            </div>
            <p className="text-2xl font-bold">{stats.usage_count || 0} / {stats.usage_limit === -1 ? '∞' : stats.usage_limit}</p>
            <p className="text-sm text-gray-500">Generations used</p>
            {stats.usage_limit !== -1 && (
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#E60000] rounded-full transition-all"
                  style={{ width: `${Math.min((stats.usage_count / stats.usage_limit) * 100, 100)}%` }}
                />
              </div>
            )}
          </div>
          
          <div className="bg-[#111] rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Layers size={20} className="text-blue-500" />
              </div>
              <span className="text-xs text-gray-500">Access</span>
            </div>
            <p className="text-2xl font-bold">{platforms.length}</p>
            <p className="text-sm text-gray-500">Platforms available</p>
          </div>
          
          <div className="bg-[#111] rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <BarChart3 size={20} className="text-green-500" />
              </div>
              <span className="text-xs text-gray-500">History</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_generations || 0}</p>
            <p className="text-sm text-gray-500">Total generations</p>
          </div>
        </div>

        {/* Platforms */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Your Platforms</h2>
          {platforms.length === 0 ? (
            <div className="bg-[#111] rounded-xl border border-white/10 p-8 text-center">
              <Layers size={40} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-500">No platforms assigned yet.</p>
              <p className="text-sm text-gray-600">Contact your admin to get access.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {platforms.map((platform) => (
                <motion.button
                  key={platform._id}
                  onClick={() => navigate(`/tool/${platform._id}`)}
                  className="bg-[#111] rounded-xl border border-white/10 p-5 text-left hover:border-[#E60000]/50 transition-all group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E60000] to-[#FF4444] flex items-center justify-center">
                      <Play size={24} className="text-white" />
                    </div>
                    <ChevronRight size={20} className="text-gray-600 group-hover:text-[#E60000] transition-colors" />
                  </div>
                  <h3 className="font-bold text-lg mb-1">{platform.name}</h3>
                  <p className="text-sm text-gray-500">Click to start generating</p>
                </motion.button>
              ))}
            </div>
          )}
        </div>

        {/* Recent Generations */}
        <div>
          <h2 className="text-xl font-bold mb-4">Recent Generations</h2>
          {generations.length === 0 ? (
            <div className="bg-[#111] rounded-xl border border-white/10 p-8 text-center">
              <Clock size={40} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-500">No generations yet.</p>
              <p className="text-sm text-gray-600">Start by selecting a platform above.</p>
            </div>
          ) : (
            <div className="bg-[#111] rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-5 py-4 text-sm font-medium text-gray-400">Platform</th>
                    <th className="text-left px-5 py-4 text-sm font-medium text-gray-400">Prompt</th>
                    <th className="text-left px-5 py-4 text-sm font-medium text-gray-400">Type</th>
                    <th className="text-left px-5 py-4 text-sm font-medium text-gray-400">Status</th>
                    <th className="text-left px-5 py-4 text-sm font-medium text-gray-400">Date</th>
                    <th className="text-right px-5 py-4 text-sm font-medium text-gray-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {generations.slice(0, 10).map((gen) => (
                    <tr key={gen._id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-5 py-4 font-medium">{gen.platform_name}</td>
                      <td className="px-5 py-4 text-sm text-gray-400 max-w-xs truncate">{gen.prompt}</td>
                      <td className="px-5 py-4">
                        <span className="text-xs px-2 py-1 bg-white/5 rounded">{gen.generation_type}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          gen.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          gen.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {gen.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {new Date(gen.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {gen.result_url && (
                          <a
                            href={gen.result_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-[#E60000] hover:bg-[#E60000]/10 rounded-lg inline-flex"
                          >
                            <Download size={16} />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboard;
